import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { FormikProps } from 'formik';
import { useRef, useState } from 'react';

import { AnalyseConclusion } from 'common/components';
import { RequestInfoForm, BusinessPartnerInfoForm } from 'common/forms';
import { formatManagementCompanyNames } from 'common/forms/ClientStatementForm/managementCompanies';
import { I18nNameSpaces } from 'common/services/i18n';

import {
  selectProtocolCustomerConfirmation,
  selectProtocolGoals,
  selectInformationsCompanyInfo,
  selectProtocolCustomerStatement,
  setAgreementBusinessPartnerInfoAction,
  setAgreementRequestInfoAction,
  selectAgreementRequestInfo,
  selectAgreementBusinessPartnerInfo,
} from '../../../../ducks';
import { RequestInfoFormValues, BusinessPartnerInfoFormValues } from 'common/models/Forms';
import { QuestionTitle, PageControls } from '../../../../components';
import { useApplyFinalValidationOnBind } from '../../../../hooks/useApplyFinalValidationOnBind';
import { createSoftContinueSession, runSoftMultiFormStepContinue } from '../../../../validation/softContinue';
import { FeatureProps } from '../../../../index';

export default function AgreementSummaryFeature({ isPrivileged }: FeatureProps) {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const customerStatement = useSelector(selectProtocolCustomerStatement);
  const confirmationValues = useSelector(selectProtocolCustomerConfirmation);
  const goalsValues = useSelector(selectProtocolGoals);
  const companyInfo = useSelector(selectInformationsCompanyInfo);

  const agreementRequestValues = useSelector(selectAgreementRequestInfo);
  const agreementBPartnerValues = useSelector(selectAgreementBusinessPartnerInfo);

  const requestFormikRef = useRef<FormikProps<RequestInfoFormValues> | null>(null);
  const businessPartnerFormikRef = useRef<FormikProps<BusinessPartnerInfoFormValues> | null>(null);
  const softContinueSessionRef = useRef(createSoftContinueSession());
  const [showSoftContinueHint, setShowSoftContinueHint] = useState(false);
  const { onMultipleFormikBound } = useApplyFinalValidationOnBind();

  function bindRequestSubmit(formik: FormikProps<RequestInfoFormValues>) {
    requestFormikRef.current = formik;
    onMultipleFormikBound([requestFormikRef.current, businessPartnerFormikRef.current]);
  }

  function bindBusinessPartnerInfoSubmit(formik: FormikProps<BusinessPartnerInfoFormValues>) {
    businessPartnerFormikRef.current = formik;
    onMultipleFormikBound([requestFormikRef.current, businessPartnerFormikRef.current]);
  }

  function handleBackClick() {
    navigate(`/form/${id}/agreement/meeting`);
  }

  async function handleContinueClick() {
    const session = softContinueSessionRef.current;

    const proceeded = await runSoftMultiFormStepContinue(
      [
        {
          formik: requestFormikRef.current,
          onStepComplete: (values) => {
            if (!isPrivileged) {
              dispatch(setAgreementRequestInfoAction(values as RequestInfoFormValues));
            }
          },
        },
        {
          formik: businessPartnerFormikRef.current,
          onStepComplete: (values) => {
            if (!isPrivileged) {
              dispatch(setAgreementBusinessPartnerInfoAction(values as BusinessPartnerInfoFormValues));
            }
          },
        },
      ],
      session,
    );

    if (!proceeded) {
      setShowSoftContinueHint(session.proceedDespiteErrorsAcknowledged);
      return;
    }

    setShowSoftContinueHint(false);
    navigate(`/form/${id}/transaction`);
  }

  function handleRequestSubmit(_values: RequestInfoFormValues) {}

  function handleBusinessInfoSubmit(_values: BusinessPartnerInfoFormValues) {}

  const declarationCompanyName = formatManagementCompanyNames(customerStatement?.b ?? [], (code) =>
    t(`clientStatementForm.b.options.${code}`, {
      ns: I18nNameSpaces.Forms,
    }),
  );
  const companyName = agreementRequestValues?.a?.trim() ? agreementRequestValues.a : declarationCompanyName;

  return (
    <>
      <QuestionTitle>{t('agreementFeature.requestInfo')}</QuestionTitle>
      <RequestInfoForm
        bindSubmit={bindRequestSubmit}
        initialValues={{
          ...agreementRequestValues,
          a: companyName,
        }}
        onSubmit={handleRequestSubmit}
      />
      <QuestionTitle>{t('agreementFeature.businessPartnerInfoForm')}</QuestionTitle>
      <BusinessPartnerInfoForm
        bindSubmit={bindBusinessPartnerInfoSubmit}
        initialValues={{
          a: companyInfo!.firstName,
          b: companyInfo!.lastName,
          c: companyInfo!.partnerNumber,
          ...agreementBPartnerValues,
        }}
        onSubmit={handleBusinessInfoSubmit}
      />
      <QuestionTitle>{t('agreementFeature.conclusion')}</QuestionTitle>
      <AnalyseConclusion
        oneTimeProvision={confirmationValues?.once ?? 0}
        monthlyProvision={confirmationValues?.monthly ?? 0}
        monthlyInvestingPlan={goalsValues?.d?.regularly ?? 0}
        oneTimeInvesting={goalsValues?.d?.amount ?? 0}
      />
      <PageControls softContinueHint={showSoftContinueHint} onBack={handleBackClick} onContinue={handleContinueClick} />
    </>
  );
}
